import { useEffect, useId, useRef, useState } from 'react';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
  chart: string;
  className?: string;
}

let mermaidInitialized = false;

function initializeMermaid() {
  if (mermaidInitialized) {
    return;
  }

  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'antiscript',
    flowchart: {
      htmlLabels: true,
      useMaxWidth: true,
    },
  });

  mermaidInitialized = true;
}

export function MermaidDiagram({ chart, className }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string>();
  const diagramId = useId().replace(/:/g, '-');

  useEffect(() => {
    let cancelled = false;

    const renderDiagram = async () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      container.innerHTML = '';
      setError(undefined);

      try {
        initializeMermaid();

        const { svg, bindFunctions } = await mermaid.render(
          `mermaid-${diagramId}`,
          chart.trim(),
        );

        if (cancelled || !containerRef.current) {
          return;
        }

        containerRef.current.innerHTML = svg;
        bindFunctions?.(containerRef.current);
      } catch (renderError) {
        if (!cancelled) {
          setError(
            renderError instanceof Error
              ? renderError.message
              : 'Unable to render Mermaid diagram.',
          );
        }
      }
    };

    renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [chart, diagramId]);

  if (error) {
    return (
      <div className={className}>
        <div className="mermaid-diagram-error" role="alert">
          Unable to render Mermaid diagram.
        </div>
        <pre>
          <code className="language-mermaid">{chart}</code>
        </pre>
      </div>
    );
  }

  return <div className={className} data-mermaid-diagram ref={containerRef} />;
}
